import type { ICellRendererParams } from '@mint-lib/ag-grid/community';
import { LoadingCell } from '@mint-lib/ag-grid/components';
import { useTranslation } from '@mint-lib/i18n';
import { Information } from '@mint-lib/icons';
import {
  Chip,
  RichTooltip,
  RichTooltipContent,
  RichTooltipTrigger,
  typographyVariants,
} from '@mint-lib/tw-components';

import { USER_STATUS } from '../../../constants/userTableV2.js';
import type { UserTableV2Row } from '../datasource.js';
import { isUserUpdating, type UserTableV2GridContext } from '../gridContext.js';
import InvitationExpiredPopover from './InvitationExpiredPopover.jsx';

type Props = ICellRendererParams<
  UserTableV2Row,
  string | undefined,
  UserTableV2GridContext
>;

type StatusConfig = {
  label: string;
  color: 'success' | 'info' | 'silver';
};

const getStatusConfig = (
  status: string,
  t: (key: string) => string,
): StatusConfig => {
  if (status === USER_STATUS.Active) {
    return { label: t('Active'), color: 'success' };
  }
  if (
    status === USER_STATUS.Pending ||
    status === USER_STATUS.PendingExistingUser ||
    status === USER_STATUS.PendingNewUser ||
    status === USER_STATUS.Expired
  ) {
    return { label: t('Pending'), color: 'info' };
  }
  return { label: t('Deactivated'), color: 'silver' };
};

const StatusCell = ({ context, data, api }: Props) => {
  const { t } = useTranslation('@myn/permissions');
  const isUpdating = isUserUpdating(context, data?.id);

  if (!data || isUpdating) {
    return <LoadingCell />;
  }

  const { label, color } = getStatusConfig(data.status, t);
  const showAttention = data.status === USER_STATUS.Expired;
  const isDeactivatedSystem = data.status === USER_STATUS.DeactivatedSystem;

  const chip = (
    <Chip size="small" variant="outline" color={color}>
      {isDeactivatedSystem ? (
        <Information
          className="size-4 shrink-0 fill-icon-secondary"
          aria-label={t('Deactivated at system level')}
        />
      ) : null}
      {label}
    </Chip>
  );

  return (
    <div className="flex h-full w-full items-center justify-between">
      {isDeactivatedSystem ? (
        <RichTooltip>
          <RichTooltipTrigger asChild>{chip}</RichTooltipTrigger>
          <RichTooltipContent side="top" className="w-75">
            <p className={typographyVariants({ variant: 'bodyLong01' })}>
              {t(
                'This user has been deactivated at the system level. Editing and reactivation are not available from this workspace. Please contact support to restore access.',
              )}
            </p>
          </RichTooltipContent>
        </RichTooltip>
      ) : (
        chip
      )}
      {showAttention ? (
        <InvitationExpiredPopover
          user={data}
          api={api}
          isReadonlyPermission={context.isReadonlyPermission}
        />
      ) : null}
    </div>
  );
};

export default StatusCell;
